import {
  generatedApi,
  type KnowledgeBaseInfo,
  type KnowledgeLinkedDocumentUploadKnowledgeLinkedDocumentUploadPostApiArg,
  type KnowledgeSyncPeriodUpdateKnowledgeSyncPeriodUpdatePostApiArg,
  type KnowledgeLinkRelatedKnowledgeLinkRelatedPostApiArg,
  type KnowledgeLinkRelatedKnowledgeLinkRelatedPostApiResponse,
} from '@shared/api/generated-api';
import type { RootState } from '@shared/redux';

type KnowledgeLinkAddRequestBody = {
  url: string;
  organization_id: number;
  title: string;
  description: string;
};

type KnowledgeLinkAddApiArg = {
  bodyKnowledgeLinkAddKnowledgeLinkAddPost: KnowledgeLinkAddRequestBody;
};

export const knowledgeBaseEndpoints = {
  getKnowledgeListKnowledgeListGet: {
    providesTags: ['knowledge'],
  },

  knowledgeBaseUpdateKnowledgeAgentUpdatePost: {
    invalidatesTags: [],
    async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
      try {
        await queryFulfilled;

        const agentInfoArgs = generatedApi.util.selectCachedArgsForQuery(
          getState() as RootState,
          'getAgentInfoAgentInfoGet',
        );

        const { agent_id, knowledge_base_files } =
          arg.bodyKnowledgeBaseUpdateKnowledgeAgentUpdatePost;

        for (const args of agentInfoArgs) {
          if (args.agentId !== agent_id) continue;

          dispatch(
            generatedApi.util.updateQueryData('getAgentInfoAgentInfoGet', args, (draft) => {
              draft.agent_knowledge = knowledge_base_files;
            }),
          );
        }
      } catch (error) {}
    },
  },

  knowledgeManualSyncKnowledgeLinkedDocumentSyncPost: {
    invalidatesTags: [],
  },

  knowledgeBaseDeleteKnowledgeRemoveDelete: {
    invalidatesTags: [],
    async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
      try {
        await queryFulfilled;

        const knowledgeListArgs = generatedApi.util.selectCachedArgsForQuery(
          getState() as RootState,
          'getKnowledgeListKnowledgeListGet',
        );

        const agentInfoArgs = generatedApi.util.selectCachedArgsForQuery(
          getState() as RootState,
          'getAgentInfoAgentInfoGet',
        );

        for (const args of knowledgeListArgs) {
          dispatch(
            generatedApi.util.updateQueryData('getKnowledgeListKnowledgeListGet', args, (draft) => {
              const index = draft.findIndex((item) => item.id === arg.knowledgeBaseId);
              if (index !== -1) {
                draft.splice(index, 1);
              }
            }),
          );
        }

        for (const args of agentInfoArgs) {
          dispatch(
            generatedApi.util.updateQueryData('getAgentInfoAgentInfoGet', args, (draft) => {
              const current = draft.agent_knowledge ?? [];
              draft.agent_knowledge = current.filter((id) => id !== arg.knowledgeBaseId);
            }),
          );
        }
      } catch (error) {}
    },
  },
} satisfies Parameters<typeof generatedApi.enhanceEndpoints>[0]['endpoints'];

export const knowledgeBaseApi = generatedApi.injectEndpoints({
  endpoints: (build) => ({
    knowledgeLinkAddKnowledgeLinkAddPost: build.mutation<KnowledgeBaseInfo, KnowledgeLinkAddApiArg>(
      {
        query: (queryArg) => ({
          url: '/knowledge/link/add',
          method: 'POST',
          body: queryArg.bodyKnowledgeLinkAddKnowledgeLinkAddPost,
        }),
        invalidatesTags: [],
        async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
          try {
            const { data } = await queryFulfilled;

            const knowledgeListArgs = generatedApi.util.selectCachedArgsForQuery(
              getState() as RootState,
              'getKnowledgeListKnowledgeListGet',
            );

            for (const queryArg of knowledgeListArgs) {
              if (
                queryArg.organizationId !==
                arg.bodyKnowledgeLinkAddKnowledgeLinkAddPost.organization_id
              ) {
                continue;
              }

              dispatch(
                generatedApi.util.updateQueryData(
                  'getKnowledgeListKnowledgeListGet',
                  queryArg,
                  (draft) => {
                    const index = draft.findIndex((item) => item.id === data.id);

                    if (index !== -1) {
                      draft[index] = data;
                      return;
                    }

                    draft.unshift(data);
                  },
                ),
              );
            }
          } catch {}
        },
      },
    ),
    knowledgeLinkRelatedKnowledgeLinkRelatedPost: build.mutation<
      KnowledgeLinkRelatedKnowledgeLinkRelatedPostApiResponse,
      KnowledgeLinkRelatedKnowledgeLinkRelatedPostApiArg
    >({
      query: (queryArg) => ({
        url: '/knowledge/link/related',
        method: 'POST',
        body: queryArg.bodyKnowledgeLinkRelatedKnowledgeLinkRelatedPost,
      }),
      invalidatesTags: [],
    }),
    knowledgeLinkedDocumentUploadKnowledgeLinkedDocumentUploadPost: build.mutation<
      KnowledgeBaseInfo,
      KnowledgeLinkedDocumentUploadKnowledgeLinkedDocumentUploadPostApiArg
    >({
      query: (queryArg) => ({
        url: '/knowledge/linked_document/upload',
        method: 'POST',
        body: queryArg.bodyKnowledgeLinkedDocumentUploadKnowledgeLinkedDocumentUploadPost,
      }),
      invalidatesTags: [],
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;

          const knowledgeListArgs = generatedApi.util.selectCachedArgsForQuery(
            getState() as RootState,
            'getKnowledgeListKnowledgeListGet',
          );

          for (const queryArg of knowledgeListArgs) {
            if (
              queryArg.organizationId !==
              arg.bodyKnowledgeLinkedDocumentUploadKnowledgeLinkedDocumentUploadPost.organization_id
            ) {
              continue;
            }

            dispatch(
              generatedApi.util.updateQueryData(
                'getKnowledgeListKnowledgeListGet',
                queryArg,
                (draft) => {
                  const index = draft.findIndex((item) => item.id === data.id);

                  if (index !== -1) {
                    draft[index] = data;
                    return;
                  }

                  draft.unshift(data);
                },
              ),
            );
          }
        } catch {}
      },
    }),
    knowledgeSyncPeriodUpdateKnowledgeSyncPeriodUpdatePost: build.mutation<
      KnowledgeBaseInfo,
      KnowledgeSyncPeriodUpdateKnowledgeSyncPeriodUpdatePostApiArg
    >({
      query: (queryArg) => ({
        url: '/knowledge/sync_period_update',
        method: 'POST',
        body: queryArg.bodyKnowledgeSyncPeriodUpdateKnowledgeSyncPeriodUpdatePost,
      }),
      invalidatesTags: [],
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;

          const knowledgeListArgs = generatedApi.util.selectCachedArgsForQuery(
            getState() as RootState,
            'getKnowledgeListKnowledgeListGet',
          );

          for (const queryArg of knowledgeListArgs) {
            dispatch(
              generatedApi.util.updateQueryData(
                'getKnowledgeListKnowledgeListGet',
                queryArg,
                (draft) => {
                  const index = draft.findIndex(
                    (item) =>
                      item.id ===
                      arg.bodyKnowledgeSyncPeriodUpdateKnowledgeSyncPeriodUpdatePost
                        .knowledge_base_id,
                  );

                  if (index !== -1) {
                    draft[index] = data;
                  }
                },
              ),
            );
          }
        } catch {}
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useKnowledgeLinkAddKnowledgeLinkAddPostMutation,
  useKnowledgeLinkRelatedKnowledgeLinkRelatedPostMutation,
  useKnowledgeLinkedDocumentUploadKnowledgeLinkedDocumentUploadPostMutation,
  useKnowledgeSyncPeriodUpdateKnowledgeSyncPeriodUpdatePostMutation,
} = knowledgeBaseApi;
